import { FC, memo } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Button } from "../../../common/components/Button/Button";
import { ShadowContainer } from "../../../common/components/Container/ShadowContainer";
import { Dropdown } from "../../../common/components/Dropdown/Dropdown";
import { InformationBlock } from "../../../common/components/InformationBlock/InformationBlock";
import { OrderItemDetails } from "../../../common/components/Order/OrderItemDetails";
import { UserInformation } from "../../../common/components/User/UserInformation";
import { Title } from "../../../common/components/Title/Title";
import { FinishedOrdersList } from "./FinishedOrdersList";
import { useGlobalModalContext } from "../../../common/components/Modal/GlobalModal";
import { FILE_TYPE, MODAL_TYPES } from "../../../common/utils/consts";
import { withErrorBoundary } from "common/utils/hoc/withErrorBoundary";
import { getErrorMessage } from "../../../common/utils/helpers";
import {
  useApproveOrderPayoutMutation,
  useRejectOrderPayoutMutation,
  useGetOrderByIdQuery,
} from "../../../services/orderApi";
import { setAvtiveOrderId } from "./finishedOrderModerationSlice";

export const FinishedOrderModerationPage: FC = withErrorBoundary(
  memo(() => {
    const activeOrderId = useAppSelector(
      (state) => state.platformManager.finishedOrderModeration.activeOrderId
    );

    const { showModal } = useGlobalModalContext();

    const dispatch = useAppDispatch();

    const { data: activeOrder } = useGetOrderByIdQuery(
      { id: activeOrderId! },
      { skip: !activeOrderId }
    );

    // Путевой лист может лежать и в photos (веб-кабинеты), и в documents
    // (приложение водителя)
    const hasWaybill = [
      ...(activeOrder?.photos ?? []),
      ...(activeOrder?.documents ?? []),
    ].some((file) => file.type.id === FILE_TYPE.WAYBILL);

    const handleCall = () => {
      if (activeOrder?.phone) {
        window.location.href = `tel:+${activeOrder.phone}`;
      }
    };

    const showReportModal = () => {
      showModal(MODAL_TYPES.REPORT_MODAL);
    };

    const [approvePayout, { isLoading: isPayoutApproving }] =
      useApproveOrderPayoutMutation();
    const [rejectPayout, { isLoading: isPayoutRejecting }] =
      useRejectOrderPayoutMutation();

    const handleApprove = async () => {
      if (!activeOrderId) return;
      await approvePayout(activeOrderId)
        .unwrap()
        .then(() => {
          dispatch(setAvtiveOrderId(undefined));
          showModal(MODAL_TYPES.SUCCESS_MODAL, {
            title: "Выплата одобрена",
          });
        })
        .catch((err) => {
          showModal(MODAL_TYPES.ERROR_MODAL, {
            title: getErrorMessage(err)?.data?.message,
            subTitle: "Не удалось одобрить выплату",
          });
        });
    };

    const handleReject = async (reason?: string) => {
      if (!activeOrderId) return;
      await rejectPayout({ id: activeOrderId, reason })
        .unwrap()
        .then(() => {
          dispatch(setAvtiveOrderId(undefined));
          showModal(MODAL_TYPES.SUCCESS_MODAL, {
            title: "Выплата отклонена",
          });
        })
        .catch((err) => {
          showModal(MODAL_TYPES.ERROR_MODAL, {
            title: getErrorMessage(err)?.data?.message,
            subTitle: "Не удалось отклонить выплату",
          });
        });
    };

    const showRejectConfirmationModal = () => {
      showModal(MODAL_TYPES.CONFIRM_MODAL, {
        title: "Отклонить выплату",
        subTitle: "Вы уверены что хотите отклонить выплату по данному заказу?",
        withComment: true,
        commentOptional: true,
        commentLabel: "Причина отклонения",
        onConfirm: (reason?: string) => handleReject(reason),
      });
    };

    return (
      <>
        <div className="grid grid-rows-[40px,40px,1fr] gap-[10px] overflow-y-hidden">
          <FinishedOrdersList />
        </div>
        {activeOrderId ? (
          <>
            <div className="mb-[15px] grid grid-rows-[1fr,210px] overflow-hidden">
              <ShadowContainer className="mb-[30px] overflow-y-scroll">
                <Dropdown title="Детали заказа" isOpen>
                  <OrderItemDetails readOnly orderId={activeOrderId} />
                </Dropdown>
              </ShadowContainer>

              <div className="grid gap-[10px]">
                <Button onClick={handleCall} disabled={!activeOrder?.phone}>
                  Позвонить
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={!hasWaybill || isPayoutApproving}
                  title={
                    hasWaybill ? undefined : "Путевой лист ещё не загружен"
                  }
                >
                  {hasWaybill
                    ? "Одобрить выплату"
                    : "Одобрить выплату (нет путевого листа)"}
                </Button>
                <Button
                  mode="darkBorder"
                  onClick={showRejectConfirmationModal}
                  disabled={isPayoutRejecting}
                >
                  Отклонить выплату
                </Button>
                <Button mode="darkBorder" onClick={showReportModal}>
                  Отправить разработчику
                </Button>
              </div>
            </div>

            <div className="mb-[15px] grid grid-rows-[35px,1fr] gap-[15px] overflow-y-hidden">
              <Title>Информация о пользователе</Title>
              {activeOrder?.account && (
                <UserInformation userId={activeOrder.account.id} />
              )}
            </div>
          </>
        ) : (
          <InformationBlock className="col-span-2 w-full" />
        )}
      </>
    );
  })
);
